<template>
  <div class="payment-grid">
    <aside class="order-summary">
      <div class="order-summary__head">
        <span class="dot"></span>
        {{ headerTitle }} · סיכום הזמנה
      </div>

      <div v-for="(it, i) in ctx.items" :key="i" class="order-item">
        <div class="order-item__row">
          <div class="order-item__name">{{ it.name }}</div>
          <div class="order-item__price">₪{{ formatNum(it.price * (it.qty || 1)) }}</div>
        </div>
        <div v-if="it.sub" class="order-item__sub">{{ it.sub }}</div>
      </div>

      <div class="order-item order-item--note">
        <div class="order-item__note-row">
          <LtIcon name="clock" :size="16" class="order-item__note-icon" />
          <div>
            <div class="order-item__note-title">{{ deliveryNote }}</div>
            <div class="order-item__note-sub">חשבונית מס תישלח למייל מיד לאחר התשלום.</div>
          </div>
        </div>
      </div>

      <div class="order-coupon">
        <label class="order-coupon__label">קוד קופון</label>
        <div class="order-coupon__row">
          <input
            v-model="couponCode"
            class="input order-coupon__input"
            :disabled="!!appliedCoupon || couponLoading || !!iframeUrl"
            placeholder="הזינו קוד הנחה"
            @keyup.enter="applyCoupon"
          />
          <button
            v-if="!appliedCoupon"
            type="button"
            class="order-coupon__btn"
            :disabled="!couponCode || couponLoading || !!iframeUrl"
            @click="applyCoupon"
          >
            {{ couponLoading ? '…' : 'החל' }}
          </button>
          <button
            v-else
            type="button"
            class="order-coupon__btn order-coupon__btn--remove"
            :disabled="!!iframeUrl"
            @click="removeCoupon"
          >
            הסר
          </button>
        </div>
        <div v-if="couponError" class="order-coupon__msg order-coupon__msg--err">
          {{ couponError }}
        </div>
        <div v-else-if="appliedCoupon" class="order-coupon__msg order-coupon__msg--ok">
          קופון <strong>{{ appliedCoupon.code }}</strong> הוחל ({{ appliedCoupon.discount_percent }}% הנחה)
        </div>
      </div>

      <div class="order-totals">
        <div class="order-totals__row">
          <span>סכום ביניים</span>
          <strong>₪{{ formatNum(subtotal) }}</strong>
        </div>
        <div v-if="discount > 0" class="order-totals__row order-totals__row--discount">
          <span>הנחה</span>
          <strong>−₪{{ formatNum(discount) }}</strong>
        </div>
        <div class="order-totals__row">
          <span>מע״מ (18%)</span>
          <strong>₪{{ formatNum(vat) }}</strong>
        </div>
      </div>

      <div class="order-divider"></div>

      <div class="order-grand">
        <span class="order-grand__label">סה״כ לתשלום</span>
        <span class="order-grand__value">₪{{ formatNum(total) }}</span>
      </div>

      <div class="order-security">
        <span class="order-security__icon"><LtIcon name="shield" :size="16" /></span>
        <div>
          התשלום מאובטח בתקן <strong>PCI-DSS</strong>.<br />
          פרטי הכרטיס מוזנים ישירות מול שרתי הסליקה ולא נשמרים אצלנו.
        </div>
      </div>
    </aside>

    <section class="payment-form">
      <div class="payment-form__head">
        <span class="payment-form__head-icon"><LtIcon name="receipt" :size="18" /></span>
        <div>
          <h2>פרטי חשבונית ותשלום</h2>
        </div>
      </div>
      <p class="payment-form__sub">החשבונית תישלח אוטומטית לכתובת המייל שתזינו.</p>

      <template v-if="!iframeUrl">
        <div class="field">
          <label class="field__label">שם מלא / חברה לחשבונית <span class="req">*</span></label>
          <input
            v-model="form.billingName"
            class="input"
            :disabled="processing"
            placeholder="למי להנפיק את החשבונית?"
          />
        </div>
        <div class="field">
          <label class="field__label">מייל לקבלת חשבונית <span class="req">*</span></label>
          <input
            v-model="form.email"
            class="input"
            type="email"
            :disabled="processing"
            placeholder="your@email.com"
          />
        </div>

        <div v-if="initError" class="payment-error">
          {{ initError }}
        </div>

        <button type="button" class="pay-cta" :disabled="!canStart || processing" @click="startPayment">
          <LtIcon v-if="!processing" name="lock" :size="16" />
          {{ processing ? 'פותח עמוד תשלום מאובטח…' : 'המשך לתשלום מאובטח' }}
          <span v-if="!processing" class="pay-cta__amount">₪{{ formatNum(total) }}</span>
        </button>

        <div class="payment-trust">
          <span class="payment-trust__item"><LtIcon name="lock" :size="12" /> SSL 256-bit</span>
          <span class="payment-trust__item"><LtIcon name="shield" :size="12" /> PCI-DSS Level 1</span>
          <span class="payment-trust__item"><LtIcon name="check" :size="12" /> סליקת Tranzila</span>
        </div>
      </template>

      <template v-else>
        <div class="payment-iframe-wrap">
          <iframe
            ref="iframe"
            :src="iframeUrl"
            class="payment-iframe"
            frameborder="0"
            allow="payment *"
          ></iframe>

          <div v-if="polling" class="payment-iframe__overlay">
            <div class="payment-iframe__spinner"></div>
            <div class="payment-iframe__overlay-text">מעדכנים את סטטוס התשלום...</div>
          </div>
        </div>

        <div v-if="pollError" class="payment-error">
          {{ pollError }}
        </div>

        <div class="payment-iframe-actions">
          <button v-if="pollError" type="button" class="btn btn--primary btn--sm" @click="resetIframe">
            נסה שוב
          </button>
          <button v-else type="button" class="btn btn--ghost btn--sm" :disabled="polling" @click="resetIframe">
            ביטול / חזרה
          </button>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import LtIcon from './LtIcon.vue';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

export default {
  name: 'PaymentPage',
  components: { LtIcon },
  props: {
    ctx: { type: Object, required: true },
  },
  emits: ['paid', 'back'],
  data() {
    return {
      form: {
        billingName: '',
        email: '',
      },
      processing: false,
      VAT_RATE: 0.18,
      couponCode: '',
      appliedCoupon: null,
      couponLoading: false,
      couponError: '',

      initError: '',

      paymentId: '',
      iframeUrl: '',

      polling: false,
      pollError: '',
      pollTimer: null,
      pollDeadline: 0,
    };
  },
  computed: {
    subtotal() {
      return (this.ctx.items || []).reduce((s, it) => s + it.price * (it.qty || 1), 0);
    },
    discount() {
      return this.appliedCoupon ? Number(this.appliedCoupon.discount_amount) || 0 : 0;
    },
    subtotalAfterDiscount() {
      return Math.max(0, this.subtotal - this.discount);
    },
    vat() {
      return Math.round(this.subtotalAfterDiscount * this.VAT_RATE * 100) / 100;
    },
    total() {
      return this.subtotalAfterDiscount + this.vat;
    },
    deliveryNote() {
      return this.ctx.kind === 'bundle'
        ? 'נטען לחשבון מיד עם אישור התשלום'
        : 'השליחה לאישור מתבצעת אוטומטית מיד לאחר התשלום';
    },
    headerTitle() {
      return this.ctx.kind === 'bundle' ? 'רכישה' : 'שליחה לאישור';
    },
    canStart() {
      if (this.processing) return false;
      if (!this.form.billingName || this.form.billingName.length < 2) return false;
      if (!/^\S+@\S+\.\S+$/.test(this.form.email)) return false;
      return true;
    },
  },
  mounted() {
    const me = this.get_me && this.get_me();
    if (me && me.email) {
      this.form.email = me.email;
    }
    if (me && me.display_name) {
      this.form.billingName = me.display_name;
    }
    window.addEventListener('message', this.onTranzilaMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onTranzilaMessage);
    this.stopPolling();
  },
  methods: {
    formatNum(n) {
      return Number(n).toLocaleString('he-IL');
    },

    applyCoupon() {
      if (!this.couponCode || this.couponLoading || this.appliedCoupon) return;
      this.couponLoading = true;
      this.couponError = '';
      this.api(
        {
          action: 'my_linktones/coupon_validate',
          data: { code: this.couponCode, amount: this.subtotal },
        },
        (res) => {
          this.couponLoading = false;
          const d = res && res.data ? res.data : res;
          if (d && d.success && d.coupon) {
            this.appliedCoupon = d.coupon;
          } else {
            this.couponError = 'הקוד אינו תקף או שפג תוקפו';
          }
        },
        () => {
          this.couponLoading = false;
          this.couponError = 'הקוד אינו תקף או שפג תוקפו';
        },
      );
    },

    removeCoupon() {
      this.appliedCoupon = null;
      this.couponCode = '';
      this.couponError = '';
    },

    startPayment() {
      if (!this.canStart || this.processing) return;
      if (this.ctx.kind !== 'bundle' || !this.ctx.product_id) {
        this.initError = 'תשלום זמין כרגע רק לרכישת חבילות';
        return;
      }

      this.processing = true;
      this.initError = '';

      this.api(
        {
          action: 'my_linktones/purchase_create',
          data: {
            product_id: this.ctx.product_id,
            coupon_code: this.appliedCoupon ? this.appliedCoupon.code : null,
            invoice_email: this.form.email,
            invoice_name: this.form.billingName,
          },
        },
        (res) => {
          this.processing = false;
          const d = res && res.data ? res.data : res;
          if (!d || !d.success || !d.iframe_url) {
            this.initError = this.errorMessageFor(d) || 'לא הצלחנו לפתוח את עמוד התשלום';
            return;
          }
          this.paymentId = d.payment_id;
          this.iframeUrl = d.iframe_url;
        },
        () => {
          this.processing = false;
          this.initError = 'שגיאת תקשורת. נסו שוב.';
        },
      );
    },

    errorMessageFor(d) {
      if (!d || !d.error) return '';
      switch (d.error) {
        case 'tranzila_not_configured':
          return 'הסליקה אינה מוגדרת במערכת. פנו למנהל האתר.';
        case 'invalid_coupon':
          return 'הקופון שהוזן אינו תקף.';
        case 'product_not_found':
          return 'המוצר לא נמצא.';
        case 'invalid_amount':
          return 'סכום לא תקין.';
        case 'handshake_failed':
          return 'התקשורת מול מערכת הסליקה נכשלה. נסו שוב בעוד מספר רגעים.';
        case 'unauthorized':
          return 'נדרשת התחברות.';
        default:
          return d.message || '';
      }
    },

    onTranzilaMessage(event) {
      const data = event && event.data;
      if (!data || data.type !== 'TRANZILA_PAYMENT_RETURN') return;
      if (!this.paymentId) return;
      this.startPolling();
    },

    startPolling() {
      if (this.polling) return;
      this.polling = true;
      this.pollError = '';
      this.pollDeadline = Date.now() + POLL_TIMEOUT_MS;
      this.checkStatus();
      this.pollTimer = setInterval(this.checkStatus, POLL_INTERVAL_MS);
    },

    stopPolling() {
      this.polling = false;
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    checkStatus() {
      if (!this.paymentId) {
        this.stopPolling();
        return;
      }

      this.api(
        {
          method: 'get',
          action: 'my_linktones/purchase_status',
          data: { payment_id: this.paymentId },
        },
        (res) => {
          const d = res && res.data ? res.data : res;
          if (!d || !d.success) return;

          if (d.status === 'paid') {
            this.stopPolling();
            const paymentId = this.paymentId;
            const couponCode = this.appliedCoupon ? this.appliedCoupon.code : null;
            this.success('התשלום בוצע בהצלחה!');
            this.$emit('paid', { paymentId, couponCode, balance: d.balance });
            return;
          }

          if (d.status === 'failed' || d.status === 'cancelled') {
            this.stopPolling();
            this.pollError = 'התשלום לא אושר.';
            return;
          }

          if (Date.now() > this.pollDeadline) {
            this.stopPolling();
            this.pollError = 'עדיין לא קיבלנו אישור מהסליקה. בדקו את האימייל או נסו שוב.';
          }
        },
        () => {
          // Ignore transient errors, keep polling until deadline.
          if (Date.now() > this.pollDeadline) {
            this.stopPolling();
            this.pollError = 'תקלת תקשורת בבדיקת סטטוס התשלום.';
          }
        },
      );
    },

    resetIframe() {
      this.stopPolling();
      this.iframeUrl = '';
      this.paymentId = '';
    },
  },
};
</script>

<style scoped>
.payment-iframe-wrap {
  position: relative;
  width: 100%;
  min-height: 640px;
  margin-top: 12px;
  border-radius: 12px;
  overflow: hidden;
  background: #f7f7f9;
}
.payment-iframe {
  width: 100%;
  min-height: 640px;
  border: 0;
  display: block;
}
.payment-iframe__overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.payment-iframe__spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #ddd;
  border-top-color: #333;
  border-radius: 50%;
  animation: payment-spin 0.9s linear infinite;
}
.payment-iframe__overlay-text {
  font-size: 14px;
  color: #444;
}
.payment-iframe-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}
.payment-error {
  color: #b00020;
  background: #fff1f1;
  border: 1px solid #f5c2c2;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 8px 0;
  font-size: 14px;
}
@keyframes payment-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
